/*
 * "Recent changes" breadcrumb (build item #6 FE half, BOARDROOM-2026-09-05-
 * 121056): rendered inside the Orders EMPTY state when the /me/holdings wire
 * carries a non-empty `removedHoldings[]` — an account that HAD a ticket
 * (transferred away / reassigned / CS-changed) must never be told "nothing
 * here yet" with no explanation (the support-ticket generator). The server
 * feed is recency-bounded (90 days, newest 10) and excludes live-held tickets,
 * so every entry here is a real, recent loss.
 *
 * One quiet line per removed holding, keyed on the actor TYPE plus — for the
 * owner — the wire's change verb (review fix F2a): a transfer-shaped change
 * ('transferred' / 'reassigned' / 'claimed-elsewhere') reads "transferred to
 * another participant … by the order owner"; a 'revoked' (or unknown) owner
 * change reads "removed by the order owner" — the seat may simply be empty
 * now, and claiming a transfer would be a fabrication. An entry with NO usable
 * actorType renders the actor-SILENT variant ("{head} — changed on {date}") —
 * never "updated automatically", which asserts a system actor we do not know
 * (review fix F2b).
 *
 * PRIVACY DEFAULT (boardroom-proposed, implemented strict): recipient
 * identity is NEVER shown — actor TYPE only. Structural, not just editorial:
 * the RemovedHolding type (myApi normalizeRemovedHoldings) carries no
 * recipient fields at all. The allowed same-order purchaser exception (naming
 * a seat's new holder the purchaser assigned themselves) is NOT implemented:
 * the wire shape carries no recipient identity, so the distinction is not
 * cheaply available — strict default ships (noted to boardroom).
 *
 * i18n home: the ONE shared `recentChanges.*` namespace (review fix F5b — the
 * former per-screen orders./getReady. twins were byte-identical duplicates).
 *
 * The contact-support link opens the ONE in-app support form
 * (CMT-2026-10-31-002, src/data/support.ts); with no support host configured
 * it is honestly absent, never a dead link.
 */
import type { CSSProperties } from 'react'
import type { RemovedHolding } from '../data/myApi'
import { SupportLink } from './SupportForm'
import { useLocale, type MessageKey } from '../i18n/useLocale'

// Deterministic short date ("5 Sep" — the dispatch's exact pattern),
// independent of the runtime's ICU month tables ("Sept" drift in en-GB).
// Arabic ships the same English placeholder as every other ar.ts value
// pending native review (PRODUCT-RULES §Localisation).
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "5 Sep" for a parseable ISO instant; null (date omitted honestly)
 *  otherwise. UTC-ANCHORED (review fix F3): the server audit log records the
 *  change instant in UTC, and the UTC calendar date is the one deterministic
 *  reading of it — local getDate()/getMonth() would show a near-midnight
 *  change under a different date per viewer timezone (and per CI runner). */
export function shortChangeDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`
}

/** Owner change verbs that truthfully read as a TRANSFER — anything else from
 *  the owner ('revoked', unknown/absent) gets the plain "removed" phrasing. */
const OWNER_TRANSFER_VERBS = new Set(['transferred', 'reassigned', 'claimed-elsewhere'])

/** The `recentChanges.*` copy-key stem for one entry (exported for tests):
 *  actor type first, then — for the owner — the change verb (F2a). A null
 *  actorType is the actor-SILENT 'unknown' stem (F2b), never 'system'. */
export function changeLineKind(e: Pick<RemovedHolding, 'actorType' | 'change'>): 'owner' | 'ownerRemoved' | 'support' | 'system' | 'unknown' {
  switch (e.actorType) {
    case 'owner':
      return e.change !== null && OWNER_TRANSFER_VERBS.has(e.change) ? 'owner' : 'ownerRemoved'
    case 'support':
      return 'support'
    case 'system':
      return 'system'
    default:
      return 'unknown'
  }
}

const lineStyle: CSSProperties = {
  fontSize: 11.5, lineHeight: 1.5, color: 'var(--tru-text-quaternary)',
  margin: '0 0 4px', overflowWrap: 'anywhere',
}

export function RemovedHoldingsNote({ entries }: { entries: RemovedHolding[] }) {
  const { t } = useLocale()
  if (entries.length === 0) return null
  const k = (leaf: string) => `recentChanges.${leaf}` as MessageKey
  return (
    <div data-testid="removed-holdings-note" style={{ marginBlockStart: 14, maxWidth: 'var(--content-max, 700px)' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tru-text-secondary)', marginBlockEnd: 6 }}>
        {t(k('title'))}
      </div>
      {entries.map((e, i) => {
        // Head = "{event} — {type}"; a type-less entry renders the event alone
        // (never a blank "— —" segment).
        const head = e.ticketTypeName ? `${e.eventName} — ${e.ticketTypeName}` : e.eventName
        const date = shortChangeDate(e.occurredAt)
        const kind = changeLineKind(e)
        const line = date ? t(k(kind), { head, date }) : t(k(`${kind}NoDate`), { head })
        return (
          <p key={`${e.eventName}|${e.occurredAt ?? ''}|${i}`} data-testid="removed-holdings-line" style={lineStyle}>
            {line}
          </p>
        )
      })}
      <SupportLink
        testid="removed-holdings-support"
        label={t(k('contactSupport'))}
        request={{ topic: 'order' }}
        style={{ fontSize: 12 }}
      />
    </div>
  )
}
